import React, { MouseEventHandler } from 'react';
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  makeStyles,
  Paper,
  PaperProps,
  Theme,
} from '@material-ui/core';
import { useTranslation } from '@fluentsms/i18n';

export type NavigationAlertDialogContentOptions = {
  confirm?: string;
  cancel?: string;
};

export type NavigationAlertDialogContent = {
  body?: string;
  options?: NavigationAlertDialogContentOptions;
};

export type NavigationAlertDialogProps = {
  open?: boolean;
  onClose?: MouseEventHandler;
  onConfirm?: MouseEventHandler;
  labeledBy?: string;
  describedBy?: string;
  navAlertContent?: NavigationAlertDialogContent;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleContainer: {
      height: theme.spacing(8),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingRight: theme.spacing(2.5),
    },
    buttonContainer: {
      height: theme.spacing(8),
      alignItems: 'center',
      padding: theme.spacing(0, 4),
      '& > :not(:first-child)': {
        marginLeft: theme.spacing(2),
      },
    },
  }),
);

export const NavigationAlertDialog = ({
  open = false,
  onClose = () => undefined,
  onConfirm = () => undefined,
  labeledBy = 'default-label',
  describedBy = 'default-description',
  navAlertContent,
}: NavigationAlertDialogProps): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation('componentLibrary');
  const navContent: NavigationAlertDialogContent = {
    body: navAlertContent?.body || t('navigationAlertDialog.navContent.body'),
    options: {
      confirm: navAlertContent?.options?.confirm || t('navigationAlertDialog.navContent.options.confirm'),
      cancel: navAlertContent?.options?.cancel || t('navigationAlertDialog.navContent.options.cancel'),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={labeledBy}
      aria-describedby={describedBy}
      PaperComponent={(props: PaperProps) => <Paper {...props} data-testid="alert-dialog" />}
    >
      <div className={classes.titleContainer}>
        <DialogTitle id={labeledBy} data-testid="alert-dialog-title">
          {t('navigationAlertDialog.navContent.title')}
        </DialogTitle>
      </div>
      <Divider />
      <DialogContent>
        <DialogContentText id={describedBy} data-testid="alert-dialog-description">
          {navContent.body}
        </DialogContentText>
      </DialogContent>
      <Divider />
      <DialogActions className={classes.buttonContainer}>
        <Button
          variant="outlined"
          onClick={onClose}
          color="secondary"
          aria-label="cancel-action"
          data-testid="cancel-action"
        >
          {navContent?.options?.cancel}
        </Button>
        <Button onClick={onConfirm} color="primary" autoFocus aria-label="confirm-action" data-testid="confirm-action">
          {navContent?.options?.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NavigationAlertDialog;
